import * as signalR from "@microsoft/signalr";

// Set window.RD_API before this script loads to override (e.g. for production).
window.RD_API ??= 'https://localhost:7008';

async function _getToken() {
    const expires = sessionStorage.getItem('rd_access_expires');
    // Refresh proactively if within 60 s of expiry.
    if (expires && new Date(expires).getTime() - Date.now() < 60_000) {
        try {
            const res = await fetch(`${window.RD_API}/auth/refresh`, {
                method: 'POST', credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                sessionStorage.setItem('rd_access_token', data.token);
                sessionStorage.setItem('rd_access_expires', data.expiresAt);
                sessionStorage.setItem('rd_user', JSON.stringify(data.user));
            }
        } catch { /* network error — proceed with current token */ }
    }
    return sessionStorage.getItem('rd_access_token') ?? '';
}

const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${window.RD_API}/hub/project`, {
        accessTokenFactory: _getToken
    })
    .withAutomaticReconnect({
        // Retry forever — exponential backoff 1s → 2s → 4s … capped at 30s, with jitter.
        nextRetryDelayInMilliseconds: ctx =>
            Math.min(1_000 * Math.pow(2, ctx.previousRetryCount), 30_000) + Math.random() * 1_000
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

// ── Connection ────────────────────────────────────────────────────────────────
window.hubConnect = function hubConnect() { return connection.start(); };
window.hubDisconnect = function hubDisconnect() { return connection.stop(); };
window.hubState = function hubState() { return connection.state; };

// ── Client → Server ───────────────────────────────────────────────────────────
window.joinProject = function joinProject(projectId) {
    return connection.invoke("JoinProject", projectId);
};
window.sendPatch = function sendPatch(pageId, patch) {
    return connection.invoke("SendPatches", pageId, [patch]);
};
window.sendPatches = function sendPatches(pageId, patches) {
    return connection.invoke("SendPatches", pageId, patches);
};
window.catchUp = async function catchUp(pageId, clientSeq) {
    return connection.invoke("CatchUp", pageId, clientSeq);
};
window.getSnapshot = function getSnapshot(pageId) {
    return fetch(`${window.RD_API}/page/snapshot?pageId=${encodeURIComponent(pageId)}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('rd_access_token') ?? ''}` },
        credentials: 'include',
    }).then(r => r.json());
};

// ── Patch event listener ──────────────────────────────────────────────────────
// Listens to the window event dispatched by CanvasManager whenever the session
// produces patches (capture, undo, redo, batch). The event name comes from
// canvasEventsLabels.patchesToSend — falls back to the literal if not yet loaded.
const _patchEventName = () => window.canvasEventsLabels?.patchesToSend ?? 'canvas.patchesToSend';
window.addEventListener(_patchEventName(), function onPatchesToSend(/** @type {CustomEvent} */ e) {
    console.log(e.detail);
    connection.invoke("SendPatches", e.detail.pageId, e.detail.patches);
});

// ── Server → Client (dispatch window events, symmetric with patchesToSend) ────
connection.on("ReceivePatches", function onReceivePatches(pageId, patches) { window.dispatchEvent(new CustomEvent("hub.receivePatches", { detail: { pageId, patches } })); });
connection.on("CatchUpPatches", function onCatchUpPatches(pageId, patches) {
    window.dispatchEvent(new CustomEvent("hub.catchUpPatches", { detail: { pageId, patches } })); });
connection.on("UserJoined", function onUserJoined(...args) {
    console.log("UserJoined:", args);
    window.dispatchEvent(new CustomEvent("hub.userJoined", { detail: args })); });
connection.on("UserLeft", function onUserLeft(userId) {console.log("UserLeft: "+ userId);
     window.dispatchEvent(new CustomEvent("hub.userLeft", { detail: userId })); });
connection.on("ForceDisconnect", function onForceDisconnect(reason) { window.dispatchEvent(new CustomEvent("hub.forceDisconnect", { detail: reason })); });

// Registration helpers kept for one-off listeners that don't need a window event.
window.onReceivePatches = function onReceivePatches(fn) { return connection.on("ReceivePatches", fn); };
window.onCatchUpPatches = function onCatchUpPatches(fn) { return connection.on("CatchUpPatches", fn); };

// ── Connection events ─────────────────────────────────────────────────────────
window.onHubReconnecting = function onHubReconnecting(fn) { return connection.onreconnecting(fn); };
window.onHubReconnected = function onHubReconnected(fn) { return connection.onreconnected(fn); };
window.onHubClose = function onHubClose(fn) { return connection.onclose(fn); };
